import {useEffect, useState, useCallback, useMemo} from 'react';
import CashProjection from './charts/CashProjection.jsx';
import RevenueForecast from './charts/RevenueForecast.jsx';
import AgingProfile from './charts/AgingProfile.jsx';
import ForwardFlows from './charts/ForwardFlows.jsx';
import LiquidityPosition from './charts/LiquidityPosition.jsx';
import {api} from '../lib/auth.js';
import {kr, monthShort} from '../lib/format.js';

const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni',
	'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const long = m => `${MONTHS[+m.slice(5, 7) - 1]} ${m.slice(0, 4)}`;
/** Swedish writes 10,3 — not 10.3. Only the runway figure carries a decimal. */
const sv = n => String(n).replace('.', ',');
/** "1 fakturor" is the tell of a template nobody read back. */
const inv = n => `${n} ${n === 1 ? 'faktura' : 'fakturor'}`;

/**
 * Labels are attached HERE rather than in the API, for the same reason lib/periods.js
 * does it for the dashboard: the server ships months, the client decides how a month is
 * written. `tick` and `label` are the two fields every chart primitive already reads.
 */
const label = r => ({...r, label: long(r.month), tick: monthShort(r.month)});

/**
 * Runway: cash, forecast revenue, receivables and payables.
 *
 * The forward half of this page is a PROJECTION and says so on every surface that shows
 * it — dashed lines, unfilled bars, a "prognos" pill on every card that depends on it,
 * and the assumptions in prose beside the charts rather than buried in a tooltip. That
 * is jabr/CLAUDE.md rule 4 and the repo-wide "never book preliminary figures" applied to
 * a forward-looking view: the page may compute what happens if nothing changes, and it
 * may not present that as knowledge.
 *
 * There are no inputs. A slider here would make this a second scenario surface beside
 * Simulering, and two surfaces that both answer "what if" eventually disagree about the
 * same month. Simulering models decisions; Runway extends the books.
 */
/**
 * The opening balance the user last typed, so it survives a reload. It is a VIEW
 * setting, not a figure: it is stored under the same reasoning as the Simulering
 * scenario (rule 8) and is likewise never sent anywhere but this one query parameter.
 */
const CASH_KEY = 'jabr.runway.cash';

export default function Runway({shadow = false}) {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [busy, setBusy] = useState(false);
	// The raw string, not a number: the field has to hold "595 720," mid-typing without
	// the value being parsed out from under the cursor.
	const [cash, setCash] = useState(() => {
		try { return localStorage.getItem(CASH_KEY) ?? ''; } catch { return ''; }
	});
	// Only an APPLIED balance triggers a fetch. Refetching on every keystroke would send
	// a request per digit and make the charts flicker through nonsense balances.
	const [applied, setApplied] = useState(() => {
		try { return localStorage.getItem(CASH_KEY) ?? ''; } catch { return ''; }
	});

	const load = useCallback(async () => {
		setBusy(true);
		try {
			const qs = new URLSearchParams();
			if (applied) qs.set('cash', applied);
			if (shadow) qs.set('shadow', '1');
			const res = await api(`/api/runway${qs.toString() ? `?${qs}` : ''}`);
			if (!res.ok) throw new Error(`API svarade ${res.status}`);
			setData(await res.json());
			setError(null);
		} catch (e) {
			setError(e.message);
		} finally {
			setBusy(false);
		}
	}, [applied, shadow]);

	useEffect(() => { load(); }, [load]);

	const apply = useCallback(value => {
		setApplied(value);
		try {
			if (value) localStorage.setItem(CASH_KEY, value);
			else localStorage.removeItem(CASH_KEY);
		} catch { /* private mode; the figure just does not survive the reload */ }
	}, []);

	// Hooks run before any guard. A `return` above them changes the hook count between
	// renders and fails with "rendered fewer hooks than expected" — a worse crash than
	// the empty state it would be guarding against.
	const rows = useMemo(() => (data
		? [...data.history.map(label), ...data.projected.map(label)] : []), [data]);
	const forward = useMemo(() => (data ? data.projected.map(label) : []), [data]);

	if (error) return <div className="state">Kunde inte hämta prognosen: {error}</div>;
	if (!data) return <div className="state">Hämtar…</div>;

	const {kpi, receivable, payable} = data;
	const cards = [
		kpi.cash_overridden
			? ['Kassa idag', `${kr(kpi.cash_opening)} kr`,
				`angivet · bokfört ${kr(kpi.cash_booked)} per ${kpi.cash_month}`, 'angivet']
			: ['Likvida medel', `${kr(kpi.cash)} kr`, `per ${kpi.cash_month}`, 'huvudbok'],
		['Burn', `${kr(kpi.burn)} kr/mån`, `snitt ${kpi.base_from}–${kpi.base_to}`, 'huvudbok'],
		['Runway, kassa/burn', kpi.runway_simple == null ? '—' : `${sv(kpi.runway_simple)} mån`,
			'bortser från utestående fordringar', 'huvudbok'],
		['Runway, framskriven', kpi.runway_projected == null
			? `> ${data.horizon} mån` : `${kpi.runway_projected} mån`,
			kpi.runway_month ? `kassan tar slut ${long(kpi.runway_month)}` : 'inom horisonten', 'prognos'],
		['Prognostiserad intäkt', `${kr(kpi.revenue_run)} kr/mån`,
			`run-rate, ${kpi.base_months} bokförda månader`, 'prognos'],
		['Kundfordringar', `${kr(receivable.total)} kr`,
			`varav ${kr(receivable.overdue)} kr förfallet · ${inv(receivable.count)}`, 'faktura'],
		['Leverantörsskulder', `${kr(payable.total)} kr`,
			`varav ${kr(payable.overdue)} kr förfallet · ${inv(payable.count)}`, 'faktura'],
		['Momsskuld', `${kr(kpi.vat_debt)} kr`,
			`per ${data.vat.as_of} · ${kr(kpi.vat_run)} kr/mån upparbetas`, 'huvudbok'],
		['Rörelsekapital, netto', `${kr(kpi.working_capital_net)} kr`,
			`fordringar minus skulder och moms`, 'faktura'],
	];

	return (
		<>
			<p className="sub">
				Kassa, prognostiserad intäkt, kundfordringar och leverantörsskulder för Pifa AB —
				bokfört till och med {data.cutoff}, därefter framskrivet.
			</p>

			<div className="toolbar">
				<label className="cashset">
					<span className="cashset__label">Kassa idag</span>
					<input className="search cashset__input" inputMode="decimal" value={cash}
						placeholder={kr(kpi.cash_booked)}
						onChange={e => setCash(e.target.value)}
						onBlur={() => apply(cash.trim())}
						onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply(cash.trim()); } }} />
					<span className="stamp">kr</span>
					{kpi.cash_overridden && (
						<button className="btn btn--sm" onClick={() => { setCash(''); apply(''); }}>
							Återställ
						</button>
					)}
				</label>
				<span className="stamp">
					{data.horizon} månader framåt · {inv(receivable.count)} öppna från kund ·
					{' '}{inv(payable.count)} öppen till leverantör · per {data.today}
				</span>
				<span className="spacer" />
				<button className="btn" onClick={load} disabled={busy}>
					{busy ? 'Hämtar…' : 'Uppdatera'}
				</button>
			</div>

			{kpi.cash_overridden && (
				<div className="panel panel--note">
					<div className="panel__title">
						Prognosen utgår från {kr(kpi.cash_opening)} kr, inte från bokförd kassa
					</div>
					<p>
						Huvudboken säger {kr(kpi.cash_booked)} kr per {kpi.cash_month} —
						{' '}{kr(Math.abs(kpi.cash_adjustment))} kr {kpi.cash_adjustment < 0 ? 'mer' : 'mindre'} än
						{' '}det angivna saldot. Skillnaden är rörelser som ännu inte bokförts. Det angivna
						saldot flyttar bara prognosens startpunkt: historiken nedan är oförändrad och
						ingenting har bokförts någonstans.
					</p>
				</div>
			)}

			{data.shadow?.active && (
				<div className="panel panel--note">
					<div className="panel__title">Prognosen räknar med skuggbokföring</div>
					<p>
						{data.shadow.count} preliminära verifikat ingår i underlaget. Burn, run-rate och
						momsskuld bygger alltså delvis på uppskattningar, inte enbart på bokförda utfall.
					</p>
				</div>
			)}

			<div className="cards">
				{cards.map(([lbl, value, hint, src]) => (
					<div className="card" key={lbl}>
						<span className="card__label"><span className="card__src">{src}</span>{lbl}</span>
						<div className="card__value">{value}</div>
						<div className={`card__hint${src === 'prognos' || src === 'angivet' ? ' card__hint--adjusted' : ''}`}>{hint}</div>
					</div>
				))}
			</div>

			<div className="panel">
				<div className="panel__title">Detta är en framskrivning, inte ett utfall</div>
				<p>
					Ingenting nedanför {data.cutoff} är ett utfall. Siffrorna visar var kassan tar vägen
					om ingenting ändras: samma intäkt, samma kostnad, inga nya kunder och inga
					prishöjningar. Antagandena är härledda ur böckerna och går inte att justera här —
					det som ska modelleras som ett beslut hör hemma i Simulering.
				</p>
				<ul className="assump">
					{data.assumptions.map(a => <li key={a}>{a}</li>)}
				</ul>
			</div>

			<div className="charts">
				<LiquidityPosition rows={rows} vatPayments={data.vat.payments} />
			</div>

			<div className="charts--quad">
				<CashProjection rows={rows} crossing={kpi.runway_month} />
				<ForwardFlows rows={forward} />
				<RevenueForecast rows={rows} />
				<AgingProfile receivable={receivable} payable={payable} />
			</div>

			<div className="charts--pair">
				<OpenItems title="Största öppna kundfordringar" items={receivable.top} who="Kund" />
				<VatSchedule vat={data.vat} />
			</div>

			<div className="charts--pair">
				<OpenItems title="Största öppna leverantörsskulder" items={payable.top} who="Leverantör" />
			</div>

			<div className="table-wrap">
				<table style={{minWidth: 720}}>
					<thead><tr>
						<th>Månad</th><th>Kundinbetalningar</th>
						<th>Ny försäljning</th><th>Leverantörsbetalningar</th>
						<th>Rörelsekostnad</th><th>Netto</th>
						<th>Utgående kassa</th>
					</tr></thead>
					<tbody>
						{forward.map(r => (
							<tr key={r.month} className={r.month === kpi.runway_month ? 'row--current' : undefined}>
								<td>{r.label}</td>
								<td>{kr(r.receivable_in)}</td>
								<td>{kr(r.sales_in)}</td>
								<td>−{kr(r.payable_out)}</td>
								<td>−{kr(r.operating_out)}</td>
								<td>{kr(r.net_cash)}</td>
								<td className="num--strong">{kr(r.cash)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<p className="foot-note">
				{kpi.cash_overridden
					? `Prognosen startar på det angivna saldot ${kr(kpi.cash_opening)} kr. Bokförd kassa är `
						+ `${kr(kpi.cash_booked)} kr och det är den siffran Dashboard visar — skillnaden är `
						+ 'obokförda rörelser, inte en rättelse av böckerna.'
					: 'Kassan är samma siffra som på Dashboard — den hämtas ur samma beräkning, inte ur en egen.'} Burn räknas på {kpi.base_months} helt bokförda månader ({kpi.base_from}–{kpi.base_to});
				den delvis bokförda månaden {data.cutoff} ingår inte, eftersom den saknar lönekörning och
				bär fakturerat men ännu ej bokfört. De två runway-talen skiljer sig med flit:
				kassa delat med burn bortser från de {kr(receivable.total)} kr som redan är fakturerade
				men inte inbetalda, medan framskrivningen driver in dem. Skillnaden mellan talen är
				just den rörelsekapitalpuckeln.
			</p>
		</>
	);
}

/**
 * The quarterly VAT calendar.
 *
 * `bokfört` is VAT the ledger already carries — a debt that exists whatever happens next.
 * `run-rate` is VAT the projection expects trading to generate, which is an assumption and
 * is marked as one. Summing the two into a single "moms att betala" column would present
 * the second with the authority of the first.
 */
function VatSchedule({vat}) {
	return (
		<div className="table-wrap">
			<div className="chart__head" style={{padding: '14px 16px 0'}}>
				<span className="chart__title">Moms att betala per kvartal</span>
				<span className="chart__hint">
					förfaller den 12:e i andra månaden efter kvartalets slut
				</span>
			</div>
			<table style={{minWidth: 420}}>
				<thead><tr>
					<th>Kvartal</th><th>Förfaller</th><th>Källa</th><th>Belopp</th>
				</tr></thead>
				<tbody>
					{vat.schedule.map((l, i) => (
						<tr key={`${l.quarter}-${l.booked}-${i}`} className={l.overdue ? 'row--current' : undefined}>
							<td>{l.quarter}</td>
							<td>{l.deadline}{l.overdue ? ' · förfallen' : ''}</td>
							<td>{l.booked ? 'bokfört' : 'run-rate'}</td>
							<td className="num--strong">{kr(l.amount)}</td>
						</tr>
					))}
				</tbody>
				<tfoot><tr>
					<td>Upparbetat per {vat.as_of}</td><td /><td>bokfört</td>
					<td className="num--strong">{kr(vat.debt)}</td>
				</tr></tfoot>
			</table>
		</div>
	);
}

/** The specific late invoice, not the aggregate — a collection conversation names a customer. */
function OpenItems({title, items, who}) {
	return (
		<div className="table-wrap">
			<div className="chart__head" style={{padding: '14px 16px 0'}}>
				<span className="chart__title">{title}</span>
			</div>
			<table style={{minWidth: 420}}>
				<thead><tr>
					<th>Nr</th><th>{who}</th><th>Förfaller</th>
					<th>Dagar</th><th>Belopp</th>
				</tr></thead>
				<tbody>
					{items.length === 0 && <tr><td colSpan={5}>Inga öppna poster.</td></tr>}
					{items.map(it => (
						<tr key={it.number}>
							<td>{it.number}</td><td>{it.name}</td><td>{it.due ?? '—'}</td>
							<td>{it.days > 0 ? `${it.days} förfallna` : `om ${-it.days}`}</td>
							<td className="num--strong">{kr(it.open)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
